import { useState } from 'react';

import { Minus, Plus } from 'lucide-react';

import { useConfirmModal } from '@/hooks/useConfirmModal';
import { cn } from '@/lib/utils';

import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const ExtendableTable = ({
  columns,
  disabled,
  hideButtons,
  onChange,
  mode = 'edit',
  value: defaultRows,
}) => {
  const [rows, setRows] = useState(
    defaultRows || [{ id: +new Date(), order: 0, value: {} }]
  );
  const { showConfirmModal } = useConfirmModal();

  const isEdit = mode !== 'static';

  const updateIntoRows = (nextRows) => {
    setRows(nextRows);
    onChange?.(nextRows);
  };

  const handleAddClick = () => {
    updateIntoRows([
      ...rows,
      { id: +new Date(), order: rows.length, value: {} },
    ]);
  };

  const handleDeleteClick = (id) => {
    showConfirmModal({
      confirmText: 'OK',
      description: `Are you sure you want to delete this row?`,
      onConfirm: () => {
        updateIntoRows(rows.filter((row) => row.id !== id));
      },
      title: 'Delete Row',
    });
  };

  const handleFieldChange = (id, name, value) => {
    updateIntoRows(
      rows.map((item) =>
        item.id === id
          ? {
              ...item,
              value:
                typeof value === 'object' && value !== null
                  ? { ...value }
                  : { ...item.value, [name]: value },
            }
          : item
      )
    );
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(({ className, label, name }) => (
                <TableHead
                  className={cn('font-semibold', className)}
                  key={name}
                >
                  {label}
                </TableHead>
              ))}
              {!hideButtons && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ id, value: fieldValue }, index) => (
              <TableRow key={id}>
                {columns.map(({ name, renderField }) => (
                  <TableCell key={name}>
                    {renderField?.(
                      {
                        id,
                        onChange: (value) => handleFieldChange(id, name, value),
                        value: fieldValue[name],
                      },
                      index,
                      fieldValue
                    )}
                  </TableCell>
                ))}
                {isEdit && !hideButtons && (
                  <TableCell>
                    <Button
                      disabled={disabled}
                      onClick={() => handleDeleteClick(id)}
                      variant="outline"
                    >
                      <Minus />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {isEdit && !hideButtons && (
        <div>
          <Button
            disabled={disabled}
            onClick={handleAddClick}
            variant="outline"
          >
            <Plus />
          </Button>
        </div>
      )}
    </>
  );
};

export default ExtendableTable;
